import { DocsLayout } from "@/components/docs-layout";
import { DocsNavigation } from "@/components/docs-navigation";

export const Withdrawals = () => {
  return (
    <DocsLayout>
      <div className="max-w-4xl mx-auto px-6">
        <h1 className="text-3xl font-bold mb-2">Deposits & Withdrawals</h1>

        <div className="text-sm text-muted-foreground mb-8">
          <p>How to deposit funds and withdraw your balance</p>
        </div>

        {/* Depositing Funds */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold mb-6">Depositing Funds</h2>
          <p className="text-muted-foreground mb-6">
            Nordex supports deposits on multiple blockchain networks. Click the "Deposit" button in the
            header to open the deposit dialog.
          </p>

          <h3 className="text-lg font-semibold mb-3">Supported chains</h3>
          <ul className="list-disc pl-6 space-y-2 text-muted-foreground mb-6">
            <li>
              <strong className="text-foreground">Solana</strong> — fast, typically arrives within 1
              minute
            </li>
            <li>
              <strong className="text-foreground">Arbitrum</strong> — medium speed, typically 3–5
              minutes
            </li>
            <li>
              <strong className="text-foreground">Ethereum</strong> — slower, typically 10–15
              minutes
            </li>
          </ul>

          <h3 className="text-lg font-semibold mb-3">Supported assets</h3>
          <p className="text-muted-foreground mb-6">USDC and USDT</p>

          <h3 className="text-lg font-semibold mb-4">How it works</h3>
          <div className="space-y-4 mb-6">
            <div className="flex gap-4">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center font-bold text-primary">
                1
              </div>
              <div>
                <p className="text-sm text-muted-foreground">
                  Click "Deposit" in the header
                </p>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center font-bold text-primary">
                2
              </div>
              <div>
                <p className="text-sm text-muted-foreground">
                  Select your asset (USDC or USDT) and blockchain network
                </p>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center font-bold text-primary">
                3
              </div>
              <div>
                <p className="text-sm text-muted-foreground">
                  Copy the deposit address shown (or scan the QR code)
                </p>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center font-bold text-primary">
                4
              </div>
              <div>
                <p className="text-sm text-muted-foreground">
                  Send funds from your wallet or exchange to the deposit address
                </p>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center font-bold text-primary">
                5
              </div>
              <div>
                <p className="text-sm text-muted-foreground">
                  Wait for confirmation — your balance updates automatically once the transaction is
                  confirmed
                </p>
              </div>
            </div>
          </div>

          <div className="p-4 bg-primary/5 border border-primary/20 rounded-lg mb-4">
            <p className="text-sm">
              <strong>Important:</strong> Send only the selected asset on the selected network to
              the deposit address. Sending the wrong asset or using the wrong network may result in
              permanent loss of funds.
            </p>
          </div>

          <p className="text-sm text-muted-foreground">
            <strong className="text-foreground">Minimum deposit:</strong> 10 USDC/USDT
          </p>
        </section>

        {/* Withdrawing Funds */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold mb-6">Withdrawing Funds</h2>
          <p className="text-muted-foreground mb-6">
            Click your wallet address/account in the header, then select "Withdraw" to open the
            withdrawal dialog.
          </p>

          <p className="text-muted-foreground mb-6">
            Currently supported: Solana (USDC). Ethereum, Arbitrum, and USDT withdrawals are coming
            soon.
          </p>

          <h3 className="text-lg font-semibold mb-4">How it works</h3>
          <div className="space-y-4 mb-6">
            <div className="flex gap-4">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center font-bold text-primary">
                1
              </div>
              <div>
                <p className="text-sm text-muted-foreground">
                  Click your account in the header and select "Withdraw"
                </p>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center font-bold text-primary">
                2
              </div>
              <div>
                <p className="text-sm text-muted-foreground">
                  Enter the withdrawal amount (minimum $10)
                </p>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center font-bold text-primary">
                3
              </div>
              <div>
                <p className="text-sm text-muted-foreground">
                  Enter or select a destination address. For wallet users, your connected wallet
                  address is used automatically. For email/social login users, enter a Solana wallet
                  address.
                </p>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center font-bold text-primary">
                4
              </div>
              <div>
                <p className="text-sm text-muted-foreground">
                  Review and confirm the withdrawal
                </p>
              </div>
            </div>
          </div>

          <p className="text-muted-foreground mb-4">
            <strong className="text-foreground">Saved addresses:</strong> If you signed in with email
            or social login, you can save frequently used withdrawal addresses with labels for quick
            access.
          </p>

          <p className="text-muted-foreground">
            Only one withdrawal can be in progress at a time. If you have a pending withdrawal, you
            must wait for it to complete or cancel it before submitting a new one.
          </p>
        </section>

        {/* Withdrawal Statuses */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold mb-6">Withdrawal Statuses</h2>
          <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
            <li>
              <strong className="text-foreground">Under Review (pending)</strong> — your withdrawal
              is being reviewed
            </li>
            <li>
              <strong className="text-foreground">Queued (approved)</strong> — approved and waiting
              to be processed
            </li>
            <li>
              <strong className="text-foreground">Sending (processing)</strong> — the transaction is
              being sent on-chain
            </li>
            <li>
              <strong className="text-foreground">Completed</strong> — funds have been sent to your
              destination address
            </li>
            <li>
              <strong className="text-foreground">Failed / Rejected</strong> — the withdrawal could
              not be completed; funds are returned to your trading balance
            </li>
          </ul>
          <p className="text-muted-foreground mt-4">
            You can cancel a pending withdrawal before it is approved. Cancelled withdrawals return
            funds to your available balance immediately.
          </p>
        </section>

        {/* Viewing Transfer History */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold mb-6">Viewing Transfer History</h2>
          <p className="text-muted-foreground">
            Go to your Account page and select the "Transfers" tab to see a combined history of all
            deposits and withdrawals with amounts, statuses, addresses, and dates.
          </p>
        </section>

        {/* Important Notes */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold mb-6">Important Notes</h2>
          <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
            <li>Withdrawals typically complete within a few minutes</li>
            <li>
              Make sure the destination address is correct — blockchain transactions are
              irreversible
            </li>
            <li>Keep some SOL in your Solana wallet to cover gas fees for on-chain operations</li>
            <li>
              Your available balance (not locked in positions) is the maximum you can withdraw
            </li>
          </ul>
        </section>

        <DocsNavigation
          previous={{ title: "Making Your First Deposit", href: "/first-deposit" }}
          next={{ title: "Placing Your First Trade", href: "/first-trade" }}
        />
      </div>
    </DocsLayout>
  );
};
